import { AutoSuggestInput } from '@popsure/dirty-swan';
import {
  associatedCountryFlagForCountryCode,
  Country,
  getAllCountries,
} from '@getpopsure/public-models';
import {
  QuestionnaireFormProps,
  QuestionnaireQuestions,
} from '../../../models';
import { useEffect, useState } from 'react';
import { filterOutCountries } from '../../../utils/filterOutCountries';
import { Header } from '../../Screen';

export type CountrySingleFormProps<
  Questionnaire extends QuestionnaireQuestions
> = {
  filterCountry?: Country[];
  locale?: Parameters<typeof getAllCountries>[0];
  placeholder?: string;
} & QuestionnaireFormProps<Questionnaire, Country>;

type Option = {
  value: string;
  leftIcon?: string;
};

export const CountrySingleForm = <
  Questionnaire extends QuestionnaireQuestions
>({
  value,
  question,
  onSubmitValue,
  featureName,
  filterCountry,
  locale,
  placeholder = '',
  setValidForSubmission,
}: CountrySingleFormProps<Questionnaire>) => {
  const [currentCountry, setCurrentCountry] = useState<Country | undefined>(
    value
  );
  const [currentOption, setCurrentOption] = useState(
    currentCountry?.name ?? ''
  );
  const [suggestions, setSuggestions] = useState<Option[]>([]);

  const countries =
    filterCountry && filterCountry.length > 0
      ? filterOutCountries(filterCountry, locale)
      : getAllCountries(locale);

  const handleSubmit = () => {
    if (!currentCountry) {
      throw new Error(
        `[${featureName}] Country value is missing for submission for question: ${question}`
      );
    }
    onSubmitValue(currentCountry);
  };

  useEffect(() => {
    setValidForSubmission(Boolean(currentCountry));
  }, [currentCountry, setValidForSubmission]);

  return (
    <Header onSubmit={handleSubmit}>
      <AutoSuggestInput
        currentOption={currentOption}
        suggestions={suggestions}
        onChange={(inputValue) => {
          setCurrentOption(inputValue);
          const selectedCountry = countries.find(
            (country) => country.name === inputValue
          );
          setCurrentCountry(selectedCountry);
        }}
        handleSuggestionSelected={(option) => {
          setCurrentOption(option.value);
          const selectedCountry = countries.find(
            (country) => country.name === option.value
          );
          setCurrentCountry(selectedCountry);
        }}
        handleSuggestionFetchRequest={(option) => {
          const filteredOptions = countries
            .filter((country) =>
              country.name.toLowerCase().startsWith(option.value.toLowerCase())
            )
            .map((country) => ({
              value: country.name,
              leftIcon: associatedCountryFlagForCountryCode(country.code),
            }));
          setSuggestions(filteredOptions);
        }}
        handleSuggestionClearRequest={() => setSuggestions([])}
        placeholder={placeholder}
      />
    </Header>
  );
};
